import { Component, EventEmitter, inject, Input, LOCALE_ID, NgZone, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core'
import { ControlContainer, NgForm, FormsModule } from '@angular/forms'
import { forkJoin, Subscription } from 'rxjs'

import { PrimeTemplate } from 'primeng/api'
import { Select } from 'primeng/select'
import { filter } from 'lodash-es'

import { Place, toPlaceForm } from '@app/interfaces/place'
import { PlacesApi } from '@app/apis/places.api'
import { DetailMode } from '@app/components/generic/generic.constants'
import type { CountryListItem } from '@app/generated/api/model'

@Component({
  selector: 'app-place-field',
  templateUrl: 'place-field.component.html',
  viewProviders: [{
    provide: ControlContainer,
    deps: [[Optional, NgForm]],
    useFactory: (ngForm: NgForm) => ngForm,
  }],
  imports: [
    Select,
    FormsModule,
    PrimeTemplate,
  ],
})
export class PlaceFieldComponent implements OnInit, OnChanges {
  public readonly placesApi = inject(PlacesApi)
  public readonly zone = inject(NgZone)
  public readonly locale = inject(LOCALE_ID)

  @Input() country?: Pick<CountryListItem, 'id' | 'code_iso' | 'code_iso_2' | 'label_en' | 'label_fr'> | null
  @Input() place?: Place | null
  @Input() required: boolean = false
  @Input() mode: string = 'normal'
  @Input() tabindex: number = -1

  @Output() placeChange = new EventEmitter<Place | null>()

  // there are 3 lists of places:
  // - one for reading
  // - one for writing / uploading
  // - one for the actual display, whose content alternates between the above 2 as a function of the form mode
  public readPlaces: Place[] = []
  public writePlaces: Place[] = []
  public places: Place[] = []

  public userLoaded: boolean = false
  public allLoaded: boolean = false

  public filterLabelField: string = 'label_en'
  protected readonly DetailMode = DetailMode

  ngOnInit (): void {
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }
  }

  ngOnChanges (changes: SimpleChanges<PlaceFieldComponent>) {
    // if a change is made to the `mode` then we switch the source of the Places between writeable and readable
    if (changes.mode) {
      if (this.readPlaces.length > 0) {
        this.places = this.getPlacesList()
      }
    }

    // Three types of change are possible:
    // 1: change to mode (normal / edit mode
    const modeChange = changes.mode
    // 2: when the country is changed, and we need to reset the Place to null
    const onlyCountryChange = changes.country && !changes.place
    // 3: when both the place and the country change, typically when the document changes on initial load
    const bothChange = changes.country && changes.place

    // If only the country changed (the user selected a new country in the list) then we set the place to null
    // because it's no longer valid for the new country
    if (onlyCountryChange) {
      this.place = null
      // Notify the metadata parent that the Place is changed (null)
      // This needs to be done in the following digest cycle to avoid the "Expression has changed after it was checked" warning,
      // because otherwise we will trigger another change from this change function, which is not nice.
      setTimeout(() => {
        this.placeChange.emit(this.place)
      })
    }

    if (onlyCountryChange || bothChange || modeChange) {
      // for any of these types of changes we need to recalculate the list of possible places
      setTimeout(() => {
        this.loadPlaces().add(() => {
          // finally, if we're in edit mode and we only have 1 place to choose from, then automatically select it from the list.
          if (this.mode === 'edition') {
            if (this.places.length === 1) {
              this.place = this.places[0]
              this.placeChange.emit(this.place)
            }
          }
        })
      })
    }
  }

  /**
   * Return the list of places according to the current mode, and the current Country selection.
   * If we are in 'normal' mode, return all the Places that exist, in case we are visualising a document for which we
   * don't have a UserConfiguration entry for.
   * Again, is this a real use case ? if not we can delete this and return always the `writePlaces`.
   * Either way, further filter the places to include only those corresponding to the selected country.
   */
  private getPlacesList (): Place[] {
    let countryFilteredPlaces: Place[] = []

    if (this.mode === 'edition') {
      countryFilteredPlaces = structuredClone(this.writePlaces)
    } else {
      countryFilteredPlaces = structuredClone(this.readPlaces)
    }
    if (this.country) {
      countryFilteredPlaces = filter(countryFilteredPlaces, (place: Place) => {
        return place.admin_zone_2?.admin_zone_1?.country?.id === this.country?.id
      })
    } else {
      // no country: clear the list of places to force a country choice beforehand
      countryFilteredPlaces = []
    }

    return countryFilteredPlaces
  }

  /**
   * Load the list of field offices for read and write operations
   */
  loadPlaces (): Subscription {
    // if we've already called the lists from the server then we don't need to do it again, return existing list
    if (this.readPlaces.length > 0) {
      this.places = this.getPlacesList()
      return Subscription.EMPTY
    }

    // prepare the 2 network calls
    const ordering = `admin_zone_2__admin_zone_1__country__${this.filterLabelField},name`
    // this one for all the readable Places
    const readPlacesObservable = this.placesApi.list({ archives_user_can_read: true, code__isnull: false, ordering: ordering, limit: 5000 })
    // this one for all the Places the user has the right to write to
    const writePlacesObservable = this.placesApi.list({ archives_user_can_write: true, code__isnull: false, ordering: ordering, limit: 5000 })

    // wait until both observables have finished and rebuild the places list accordingly.
    return forkJoin([readPlacesObservable, writePlacesObservable]).subscribe((combinedResults) => {
      this.readPlaces = combinedResults[0].results.map(toPlaceForm)
      this.writePlaces = combinedResults[1].results.map(toPlaceForm)
      this.places = this.getPlacesList()
    })
  }

  /**
   * the user selected a new place in the dropdown, notify the metadata parent
   */
  onSelectNewPlace () {
    this.placeChange.emit(this.place)
  }
}
